import BoolResult from "@/components/BoolResult";
import Loading from "@/components/Loading";
import ScoresComponent from "@/components/scores";
import StringResult from "@/components/StringResult";
import { Box, Breadcrumbs, Divider, Paper, Typography, Link } from "@mui/material";
import { useUserDetailsRequest } from "apiRequests/users";
import { BookOpen, Info, User2Icon } from "lucide-react";
import { useParams } from "react-router-dom";

const colors = {
    low: '#d61f69',
    medium: '#f79009',
    high: '#00d37f',
} 


const iconColor = "#5f5faf"

const UserDetailsPage = () => {
    const params = useParams()
    let { data, loading } = useUserDetailsRequest(params?.userId)
    if (typeof data === "string") {
        data = undefined
    }
    const scores = data?.scores.map(({id, name, score}) =>{
        let color;
        if(score >= 66){
            color = colors.high
        }
        if(score >= 33 && score < 66){
            color = colors.medium
        }
        if(score < 33){
            color = colors.low
        }
        return {
            id,
            name,
            score,
            color
        }
    }) ?? []

    const personalInformation = data?.answer?.data?.survey?.personalInformation
    const academics = data?.answer?.data?.survey?.academics
    const schoolLocation :string[] =[]
    if(academics?.city) schoolLocation.push(academics?.city)
    if(academics?.state) schoolLocation.push(academics?.state)

    return (
        <div>
            <Loading loading={loading} />
            <h1>User Details</h1>
            <Breadcrumbs separator=" / " aria-label="breadcrumb">
                <Link color="primary" href="/users/all">
                    All Users
                </Link>
                <Typography color="text.primary">User Details</Typography>
            </Breadcrumbs>
            <br />
            <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '250px' }}
                gap={4}
                sx={{ marginBottom: 4 }}
            >
                <Paper sx={{ p: 2 }} style={{ textAlign: 'center', padding: '60px 10px' }}>
                    <div style={{ borderRadius: '50%', backgroundColor: '#f0f0f0', display: 'inline-block', width: '90px', height: '90px', textAlign: 'center', lineHeight: '125px' }}>
                        <User2Icon size={50} color={iconColor} />
                    </div>
                    <Typography style={{ fontSize: '20px', marginTop: '10px' }}>
                        {data?.firstName} {data?.lastName}
                    </Typography>
                    {data?.email}
                </Paper>
            </Box>
            <Box             
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                gap={4}

            >
                <Box>
                    <Paper sx={{ p: 2 }} style={{padding: "1em"}}>
                        <Info style={{verticalAlign: "middle" }} color={iconColor} /> <span style={{fontWeight: "bold"}}>Personal Info</span>
                        <br />
                        <br />
                        <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={2}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Ethnicity
                                </Typography>
                                <Typography variant="body1">
                                    <StringResult>{personalInformation?.ethnicity}</StringResult>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Gender
                                </Typography>
                                <Typography variant="body1">
                                    <StringResult>{personalInformation?.gender}</StringResult>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Leadership
                                </Typography>
                                <Typography variant="body1">
                                    <StringResult>{personalInformation?.leadership}</StringResult>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Zipcode
                                </Typography>
                                <Typography variant="body1">
                                    <StringResult>{personalInformation?.zipcode}</StringResult>
                                </Typography>
                            </Box>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        <Box>
                            <Typography variant="body2" color="text.secondary">Father Education</Typography>
                            <Typography variant="body1">
                                <StringResult>{personalInformation?.father?.map(({name})=>(name)).join(", ")}</StringResult>
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                            <Typography variant="body2" color="text.secondary">Mother Education</Typography>
                            <Typography variant="body1">
                                <StringResult>{personalInformation?.mother?.map(({name})=>(name)).join(", ")}</StringResult>
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(3, 1fr)' }} gap={2}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Local Organizations</Typography>
                                <Typography variant="body1">
                                    <BoolResult>{personalInformation?.localOrganizations}</BoolResult>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">National Organizations</Typography>
                                <Typography variant="body1">
                                    <BoolResult>{personalInformation?.nationalOrganizations}</BoolResult>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Organization Awards</Typography>
                                <Typography variant="body1">
                                    <BoolResult>{personalInformation?.organizationAwards}</BoolResult>
                                </Typography>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(2, 1fr)' }} gap={2}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">School Awards</Typography>
                                <Typography variant="body1">
                                    <BoolResult>{personalInformation?.schoolAwards}</BoolResult>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">State Regional</Typography>
                                <Typography variant="body1">
                                    <BoolResult>{personalInformation?.stateRegional}</BoolResult>
                                </Typography>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', md: '1fr' }} gap={2}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Languages</Typography>
                                <Typography variant="body1">
                                    <StringResult>{personalInformation?.languages?.join(", ")}</StringResult>
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
                <Box>
                    <Paper sx={{ p: 2 }} style={{padding: "1em"}}>
                        <BookOpen style={{verticalAlign: "middle"}} color={iconColor} /> <span style={{fontWeight: "bold"}}>Academic Info</span>
                        <br />
                        <br />
                        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr 1fr' }} gap={2}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">School Name</Typography>
                                <Typography variant="body1">
                                    <StringResult>{academics?.name}</StringResult>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">School Location</Typography>
                                <Typography variant="body1">
                                    <StringResult>{schoolLocation.join(", ")}</StringResult>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Graduation Year</Typography>
                                <Typography variant="body1">
                                    <StringResult>{academics?.graduationYear}</StringResult>
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box display="grid" gridTemplateColumns="1fr 2fr 1fr 1fr" gap={2}>
                            <Box key={"Class Rank"}>
                                <Typography variant="body2" color="text.secondary">Class Rank</Typography>
                                <Typography variant="body1">
                                    <StringResult>{academics?.classRank}</StringResult>
                                </Typography>
                            </Box>
                            <Box key={"Curriculum difficulty"}>
                                <Typography variant="body2" color="text.secondary">Curriculum difficulty</Typography>
                                <Typography variant="body1">
                                    <StringResult>{academics?.curriculumDifficulty}</StringResult>
                                </Typography>
                            </Box>
                            <Box key={"GPA"}>
                                <Typography variant="body2" color="text.secondary">GPA</Typography>
                                <Typography variant="body1">
                                    <StringResult>{academics?.gpa}</StringResult>
                                </Typography>
                            </Box>
                            <Box key={"Grade"}>
                                <Typography variant="body2" color="text.secondary">Grade</Typography>
                                <Typography variant="body1">
                                    <StringResult>{academics?.grade}</StringResult>
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Test Type</Typography>
                                <Typography variant="body1">
                                    <StringResult>{academics?.testType}</StringResult>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">SAT Highest Math</Typography>
                                <Typography variant="body1">
                                    <StringResult>{academics?.satHighestMathScore}</StringResult>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">SAT Highest Language</Typography>
                                <Typography variant="body1">
                                    <StringResult>{academics?.satHighestLanguageScore}</StringResult>
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Box>
            <Box sx={{ marginTop: 4 }}>
                <Paper sx={{ p: 2 }} style={{ padding: '1em', paddingBottom: '0px' }}>
                    <Typography style={{ fontSize: '20px' }} sx={{ mb: 2 }}>
                        Odds Calculator Results
                    </Typography>
                    <ScoresComponent scores={scores} />
                </Paper>
            </Box>
        </div>
    );
}

export default UserDetailsPage
